// GNÅ-telemetri rett til SKADE over HTTPS — uten Matrix som mellomledd.
//
// EIERBESLUTNING 2026-08-13, etter en OPSEC-gjennomgang eieren startet
// («ukryptert høres skummelt ut»): posisjoner, geotagger og sensordata skal IKKE
// gå gjennom Matrix i det hele tatt. De er telemetri, ikke samtaler.
//
// HVA SOM VAR GALT MED MATRIX-VEIEN, kort:
//  1. E2EE knakk i praksis. Broens nøkkellager er i minnet (Node har ingen
//     IndexedDB), så broen får ny device-id ved hver omstart og telefonene har
//     ikke delt megolm-nøkler med den. Posisjonene kom aldri fram.
//  2. Alternativet «ukryptert Matrix-rom» ville løst leveransen, men lagt
//     posisjonshistorikken i klartekst i Synapse sin database — altså gjort
//     hjemmetjeneren til et mål som bærer vennlige styrkers posisjoner.
//  3. Metadata lekket uansett: E2EE skjuler nyttelasten, ikke HVEM som sender,
//     HVOR OFTE eller NÅR en enhet slutter å rapportere.
//
// HVORFOR HTTPS-VEIEN ER STRENGT BEDRE, ikke bare enklere:
//  - Synapse ser aldri posisjonene. Ikke innhold, ikke metadata.
//  - Ingen nøkkeldeling → ingen tapte leveranser ved omstart.
//  - Attribusjon per bruker. Dette er det viktigste: et LEKKET globalt
//    ingest-token ville latt hvem som helst injisere falske VENNLIGE posisjoner
//    i situasjonsbildet. Å forgifte bildet er verre enn å lekke det.
//
// IDENTITET (se `matrix-openid.ts` i hubben): telefonen sender IKKE sitt
// Matrix-access-token til SKADE. Den ber hjemmetjeneren om et kortlevd
// OpenID-token (Matrix-spec: `/user/{id}/openid/request_token`), som BARE beviser
// identitet og ikke kan lese meldinger. Hubben slår det opp mot Synapse.
// Kompromitteres hubben, ligger det ingen fullmakts-legitimasjon der.
import { z } from 'zod';
import { GNA_BFT_SOURCE } from './cot.js';
import { PLAUSIBILITET_NOKKEL, vurderPlausibilitet } from './plausibilitet.js';
import { KapabilitetType } from './kapabilitet.js';
import { RfiPriority } from './rfi.js';
/**
 * Hvor lenge et posisjonsspor regnes som ferskt når telefonen ikke sier noe.
 * MÅ være større enn appens sendeintervall (5 min), ellers blinker operatøren
 * inn og ut av situasjonsbildet mellom to sendinger.
 */
export const POSISJON_FERSKHET_MS = 6 * 60_000;
/** Grense for hvor gammel en innsendt måling kan være og fortsatt tas imot.
 * En time gammel posisjon er ikke et situasjonsbilde, den er historie — og en
 * kø som tømmes etter et langt nettbrudd skal ikke flytte folk tilbake i tid. */
export const POSISJON_MAKS_ALDER_MS = 30 * 60_000;
/** Hvor langt inn i FRAMTIDA et tidsstempel får ligge (klokkeskeiv på telefon). */
export const POSISJON_MAKS_FRAMTID_MS = 2 * 60_000;
/**
 * Én posisjonsrapport fra en GNÅ-telefon.
 *
 * Felt-for-felt speiler dette hva `Geolocator` faktisk gir oss i appen — ikke
 * et ønskeskjema. Alt utenom posisjon og tid er valgfritt, fordi en telefon
 * innendørs har hverken kurs, fart eller høyde, og et påkrevd felt der ville
 * tvunget appen til å dikte opp en verdi.
 */
export const GnaPosisjonSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** Måletidspunkt (ms epoch UTC) — fra GPS-en, ikke fra da vi mottok den. */
    tsMs: z.number().int().positive(),
    /** Nettleserens/OS-ets oppgitte usikkerhet i meter. Blir CEP i kartet. */
    noyaktighetM: z.number().min(0).max(100_000).optional(),
    /** Høyde over ellipsoide (HAE), slik GPS-en gir den. */
    hoydeM: z.number().min(-1000).max(30_000).optional(),
    kursGrader: z.number().min(0).max(360).optional(),
    fartMs: z.number().min(0).max(1000).optional(),
    /** Batterinivå 0–100, om appen kjenner det. Operativt relevant: en telefon
     * på 4 % slutter snart å rapportere, og TOC bør vite det før det skjer. */
    batteriProsent: z.number().int().min(0).max(100).optional(),
    /** Hva som utløste rapporten. `nød` skal aldri stilles i kø bak vanlige. */
    grunn: z.enum(['periodisk', 'manuell', 'nod']).default('periodisk'),
});
/** Én innsending kan bære flere målinger (kø tømt etter nettbrudd). */
export const GnaPosisjonPakkeSchema = z.object({
    posisjoner: z.array(GnaPosisjonSchema).min(1).max(50),
});
// ── GEOTAG OG NØDALARM OVER SAMME AUTENTISERTE VEI ─────────────────────────
//
// EIERFUNN 2026-08-13: «Jeg er på SKADE på FOH nivå, da bør jeg jo se alt!
// Hvorfor er ikke dette 1:1» — telefonen viste geotagger og markører som ikke
// fantes i COP-en.
//
// Årsaken var at BARE posisjon var flyttet til HTTPS. Geotag og SOS gikk fortsatt
// som Matrix-rommeldinger, og den veien er brutt: broens nøkkellager er i minnet,
// så den får ny device-id ved hver omstart og kan ikke dekryptere. «1:1» var
// dermed umulig uansett hvor mye vi rettet i UI-et.
//
// De to nye hendelsene her lukker det. Merk hva som IKKE flyttes: BILDET i en
// geotag blir liggende som vedlegg i Matrix-rommet, der mennesker leser det.
// Det er punktet og teksten SKADE trenger for situasjonsbildet, og et bilde er
// ikke telemetri. Det er en ærlig, bevisst grense — ikke en glemt del.
/** Et geotagget observasjonspunkt fra felt (punkt + tekst, ikke bildet). */
export const GnaGeotagInnSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    tsMs: z.number().int().positive(),
    noyaktighetM: z.number().min(0).max(100_000).optional(),
    retningGrader: z.number().min(0).max(360).optional(),
    /** Operatørens egen tekst. Kort med vilje: dette er en kartmarkør, ikke en rapport. */
    tekst: z.string().max(400).optional(),
    /** Refererer bildet som ligger i Matrix-rommet, når appen har en id. */
    bildeRef: z.string().max(256).optional(),
});
/** Nødalarm (SOS) med posisjon. `aktiv: false` avlyser en tidligere alarm. */
export const GnaNodSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    tsMs: z.number().int().positive(),
    aktiv: z.boolean().default(true),
    tekst: z.string().max(400).optional(),
});
/**
 * En BESTILLING sendt fra felt (eiermål 2026-08-19: «lag system for
 * bestilling og plassering, i GNÅ og SKADE»). Samme autentiserte vei som
 * geotag/nødalarm over — mannen i felt trenger IKKE en SKADE-stab-innlogging
 * for å be om en kapabilitet eller melde seg selv tilgjengelig som observatør;
 * OpenID-beviset appen alt har er nok, akkurat som for en geotag.
 *
 * `onsketType: 'soldat-mobil'` UTEN `omraade` er den enkleste bestillingen som
 * finnes: «jeg er her og observerer» — meg selv som kapabilitet, ikke en
 * forespørsel om noen ANNEN ressurs. Se `kapabilitet.ts`.
 */
export const GnaBestillingInnSchema = z.object({
    tsMs: z.number().int().positive(),
    behovTekst: z.string().max(2000),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    radiusM: z.number().positive().max(500_000).optional(),
    onsketType: KapabilitetType.optional(),
    hastegrad: RfiPriority.default('routine'),
});
/**
 * Én telemetri-hendelse fra GNÅ. Diskriminert union, så nye typer kan legges til
 * uten å røre de gamle — og så hubben kan avvise en ukjent type i stedet for å
 * tolke den som noe annen.
 */
export const GnaTelemetriHendelseSchema = z.discriminatedUnion('type', [
    GnaPosisjonSchema.extend({ type: z.literal('posisjon') }),
    GnaGeotagInnSchema.extend({ type: z.literal('geotag') }),
    GnaNodSchema.extend({ type: z.literal('nod') }),
    GnaBestillingInnSchema.extend({ type: z.literal('bestilling') }),
]);
export const GnaTelemetriPakkeSchema = z.object({
    hendelser: z.array(GnaTelemetriHendelseSchema).min(1).max(50),
});
/**
 * Merk sporet hvis punktet ikke er troverdig.
 *
 * Alle tre GNÅ-hendelsene går gjennom denne, fordi de kommer fra SAMME
 * enhetsposisjon: kjører telefonen på en simulator, er geotaggen og nødalarmen
 * like falske som den periodiske rapporten. Å bare merke den ene ville gitt et
 * bilde der posisjonen er merket uplausibel mens observasjonen ti meter unna
 * står uten forbehold.
 *
 * ⚠ NØKKELEN SIER «Posisjon», IKKE «Alarm» — det er viktig for nødalarmen.
 * Forbeholdet gjelder HVOR meldingen kom fra, aldri OM den er reell. En operatør
 * skal lese «jeg kan ikke stole på punktet, men noen roper», ikke «dette kan du
 * se bort fra».
 */
function leggPlausibilitet(extra, p) {
    const v = vurderPlausibilitet(p);
    if (!v.plausibel && v.merknad)
        extra[PLAUSIBILITET_NOKKEL] = v.merknad;
}
/**
 * Geotag → observasjonsspor.
 *
 * ⚠ BEVISST FORSKJELL FRA DEN GAMLE ROM-VEIEN: `geotagTilSpor` i `medlemskap.ts`
 * gjenbrukte PERSONENS spor-id, altså flyttet personen til geotaggens punkt. Her
 * blir geotaggen et EGET punkt med egen id.
 *
 * Hvorfor det er riktigere: en geotag er en OBSERVASJON av et sted, og den skal
 * bli liggende der operatøren tok den, også etter at hun har gått videre. Med
 * personens id ble hver ny geotag skrevet oppå den forrige, og TOC mistet
 * observasjonen i det soldaten flyttet seg. Personens posisjon kommer fra den
 * periodiske rapporteringen — de to skal ikke konkurrere om samme markør.
 *
 * Id-en inneholder tidspunktet, så flere geotagger fra samme person stables som
 * separate punkt. `bevares` fordi en observasjon ikke skal forsvinne av seg selv.
 */
export function geotagInnTilSpor(g, opts) {
    const kort = opts.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
    const avsender = (opts.navn ?? '').trim() || kort;
    const tekst = (g.tekst ?? '').trim();
    const spor = {
        id: `geotag:${kort}:${g.tsMs}`,
        // Et observasjonspunkt, ikke en enhet: 'point' er nettopp kategorien for
        // «noe er rapportert HER» uten at det er en styrke eller en sensor.
        kind: 'point',
        affiliation: 'unknown', // observasjonen sier ikke hvem stedet tilhører
        lat: g.lat,
        lon: g.lon,
        sidc: 'SUGP------*****',
        label: tekst ? `GEOTAG: ${tekst.slice(0, 60)}` : `GEOTAG · ${avsender}`,
        tsMs: g.tsMs,
        source: GNA_GEOTAG_SOURCE,
        bevares: true,
        person: { matrixId: opts.gnaMatrixId, navn: opts.navn },
    };
    if (g.noyaktighetM !== undefined)
        spor.cepM = g.noyaktighetM;
    const extra = {};
    leggPlausibilitet(extra, g); // først, se merknad i posisjonTilSpor
    extra['Rapportert av'] = avsender;
    if (tekst)
        extra['Tekst'] = tekst;
    if (g.retningGrader !== undefined)
        extra['Retning'] = `${Math.round(g.retningGrader)}°`;
    if (g.bildeRef)
        extra['Bilde'] = 'ligger i GNÅ-rommet';
    spor.extra = extra;
    return spor;
}
/** Kildemerke for geotagger som kom over HTTPS-veien. */
export const GNA_GEOTAG_SOURCE = 'gna-geotag';
/**
 * Nødalarm → spor.
 *
 * Alarmen kringkastes OGSÅ som collab-hendelse `emergency` (som COP-en alt
 * tegner og roper om), men den skal i tillegg finnes som et spor: en alarm som
 * bare er en toast forsvinner i det operatøren blar videre, og da er posisjonen
 * borte. `bevares` er ikke til diskusjon her.
 */
export function nodTilSpor(n, opts) {
    const kort = opts.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
    const navn = (opts.navn ?? '').trim() || kort;
    const extra = {};
    leggPlausibilitet(extra, n);
    if (n.tekst?.trim())
        extra['Melding'] = n.tekst.trim();
    return {
        id: `nod:${kort}`,
        kind: 'person',
        affiliation: 'friend',
        lat: n.lat,
        lon: n.lon,
        sidc: 'SFGPU-----*****',
        label: `NØD · ${navn}`,
        tsMs: n.tsMs,
        source: GNA_NOD_SOURCE,
        bevares: true,
        person: { matrixId: opts.gnaMatrixId, navn: opts.navn },
        extra: Object.keys(extra).length ? extra : undefined,
    };
}
/** Kildemerke for nødalarmer over HTTPS-veien. */
export const GNA_NOD_SOURCE = 'gna-nod';
/**
 * Valider én rapport OG vurder tidsstemplet mot nå.
 *
 * Tidsvurderingen ligger her, ikke i hubben, fordi det er en REGEL om data og
 * ikke en transportdetalj — og fordi den da kan testes uten en server. Grunnen
 * til at den finnes: uten den kunne en telefon med feil klokke, eller en kø som
 * tømmes etter et langt nettbrudd, skrive et gammelt punkt oppå et nyere og
 * flytte en soldat bakover i situasjonsbildet.
 */
export function vurderPosisjon(raa, naaMs) {
    const p = GnaPosisjonSchema.safeParse(raa);
    if (!p.success)
        return { ok: false, grunn: 'ugyldig' };
    const alder = naaMs - p.data.tsMs;
    if (alder > POSISJON_MAKS_ALDER_MS)
        return { ok: false, grunn: 'for-gammel' };
    if (alder < -POSISJON_MAKS_FRAMTID_MS)
        return { ok: false, grunn: 'fra-framtida' };
    return { ok: true, posisjon: p.data };
}
/**
 * Stabil spor-id for en GNÅ-bruker.
 *
 * MÅ være deterministisk av Matrix-id-en: to rapporter fra samme telefon skal
 * oppdatere ETT spor, ikke legge igjen en sti av markører i kartet. Domenet er
 * med, ellers ville to brukere med samme localpart på ulike hjemmetjenere
 * kollidert til ett symbol.
 */
export function posisjonSporId(gnaMatrixId) {
    return `gna:${gnaMatrixId.replace(/^@/, '')}`;
}
/**
 * Posisjonsrapport → SKADE-spor.
 *
 * Sporet merkes `kind: 'person'` og `source: GNA_BFT_SOURCE` FØR berikelse, slik
 * at en telefon aldri kan forveksles med en SPARK-sensor eller et TAK-importert
 * kjøretøy. Navn og grad settes senere av `berikPersonspor` i hubben, som eier
 * ORBAT-bindingen — her har vi bare identiteten OpenID-tokenet beviste.
 */
export function posisjonTilSpor(p, opts) {
    const localpart = opts.gnaMatrixId.replace(/^@/, '').replace(/:.*$/, '');
    const spor = {
        id: posisjonSporId(opts.gnaMatrixId),
        kind: 'person',
        affiliation: 'friend', // en autentisert GNÅ-bruker er per definisjon egen styrke
        lat: p.lat,
        lon: p.lon,
        // Egen bakkeenhet (APP-6): vennlig · bakke · enhet.
        sidc: 'SFGPU-----*****',
        label: (opts.kallesignal ?? '').trim() || localpart.toUpperCase(),
        tsMs: p.tsMs,
        source: GNA_BFT_SOURCE,
        person: { matrixId: opts.gnaMatrixId },
        // Lengre enn sendeintervallet, ellers står sporet «gammelt» i det halve
        // minuttet før neste rapport og operatøren blinker mellom fersk og gammel.
        ferskhetMs: POSISJON_FERSKHET_MS,
        // ET MENNESKE SLETTES IKKE AV EN TIDSAVBRUDD. Slutter telefonen å
        // rapportere, er siste kjente posisjon det viktigste vi har — den skal ligge
        // til en operatør bevisst slipper sporet. Se `bevares` i track.ts.
        bevares: true,
    };
    // CEP kun når vi FAKTISK har en usikkerhet. En hardkodet «0 m» ville sagt at
    // posisjonen er eksakt, og det er en løgn et sikte kan bygge på.
    if (p.noyaktighetM !== undefined)
        spor.cepM = p.noyaktighetM;
    if (p.hoydeM !== undefined) {
        spor.hoydeM = p.hoydeM;
        // GPS gir høyde over ellipsoiden. Referansen MÅ følge tallet (se Track).
        spor.hoydeReferanse = 'hae';
    }
    const extra = {};
    // FORBEHOLDET SETTES INN FØRST, og det er ikke kosmetikk: detaljvisningen
    // rendrer `extra` i innsettingsrekkefølge, så en advarsel plassert etter
    // «Batteri» blir lest sist — eller ikke i det hele tatt. Se plausibilitet.ts
    // for hvorfor et uplausibelt punkt merkes framfor å avvises.
    leggPlausibilitet(extra, p);
    if (p.kursGrader !== undefined)
        extra['Kurs'] = `${Math.round(p.kursGrader)}°`;
    if (p.fartMs !== undefined)
        extra['Fart'] = `${(p.fartMs * 3.6).toFixed(1)} km/t`;
    if (p.batteriProsent !== undefined)
        extra['Batteri'] = `${p.batteriProsent} %`;
    if (p.grunn !== 'periodisk')
        extra['Utløst av'] = p.grunn === 'nod' ? 'NØD' : 'manuell';
    if (Object.keys(extra).length)
        spor.extra = extra;
    return spor;
}
//# sourceMappingURL=gna-posisjon.js.map